import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export async function exportDepositSummaryReport(apiData, fileName = 'Deposit_Summary_Report.xlsx') {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Deposits Summary');

    const dataArray = Array.isArray(apiData) ? apiData : [];
    if (!dataArray.length) {
        console.error('Invalid API data structure:', apiData);
        return;
    }

    // Extract branches and products
    const branches = dataArray.map(b => b.branchName);
    const allProducts = [...new Set(
        dataArray.flatMap(branch =>
            branch.productSummaryDtoList.map(p => normalize(p.productName))
        )
    )];


    const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } },
        alignment: { vertical: 'middle', horizontal: 'center' },
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };
    const totalStyle = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '5a6c8c' } },
        alignment: { vertical: 'middle', horizontal: 'center' },
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };

    const cellBorder = {
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };

    // Title
    sheet.mergeCells(1, 1, 1, 2 + branches.length * 2);
    const titleCell = sheet.getCell('A1');
    titleCell.value = 'DEPOSITS SUMMARY REPORT';
    Object.assign(titleCell, headerStyle);

    // Header Rows
    const row2 = ['NO', 'Deposits Type'];
    const row3 = ['', ''];

    branches.forEach(branch => {
        row2.push(branch, '');
        row3.push('Number of Accs', "Amount");
    });
    sheet.addRow(row2);
    sheet.addRow(row3);

    sheet.mergeCells(2, 1, 3, 1); // Merge A2:A3 (NO)
    sheet.mergeCells(2, 2, 3, 2);

    // Merge branch headers
    let col = 3;
    branches.forEach(() => {
        sheet.mergeCells(2, col, 2, col + 1);
        Object.assign(sheet.getCell(2, col), headerStyle);
        Object.assign(sheet.getCell(3, col), headerStyle);
        Object.assign(sheet.getCell(3, col + 1), headerStyle);
        col += 2;
    });

    sheet.getRow(2).eachCell(cell => Object.assign(cell, headerStyle));

    // Fill data rows
    allProducts.forEach((product, index) => {
        const row = [index + 1, formatProductName(product)];
        dataArray.forEach(branch => {
            const found = branch.productSummaryDtoList.find(p => normalize(p.productName) === normalize(product));
            row.push(found?.count || 0);
            row.push(found?.amount || 0);
        });
        const addedRow = sheet.addRow(row);
        addedRow.eachCell(cell => cell.border = headerStyle.border);
        addedRow.eachCell((cell, colNum) => {
            if (colNum === 1 && colNum == 2) return;
            const mod = (colNum - 1) % 2;
            if (mod === 0) cell.numFmt = '#,##0';       // count
            else if (mod === 1) cell.numFmt = '#,##0.00';  // amount
            Object.assign(cell, cellBorder);
        });

        // Alternate row shading
        if ((addedRow.number - 2) % 2 === 0) {
            addedRow.eachCell((cell, colNum) => {
                if (colNum > 1) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'F2F2F2' }
                    };
                }
            });
        }
    });

    // Totals row
    const totalRow = ['Total', ''];
    for (let i = 0; i < branches.length; i++) {
        const countCol = 3 + i * 2;
        const amountCol = countCol + 1;
        const countTotalFormula = `SUM(${sheet.getColumn(countCol).letter}4:${sheet.getColumn(countCol).letter}${13})`;
        const amountTotalFormula = `SUM(${sheet.getColumn(amountCol).letter}4:${sheet.getColumn(amountCol).letter}${13})`;
        totalRow.push({ formula: countTotalFormula });
        totalRow.push({ formula: amountTotalFormula });

    }
    const addedTotalRow = sheet.addRow(totalRow);
    addedTotalRow.font = { bold: true };
    // addedTotalRow.eachCell(cell => cell.border = headerStyle.border);
    addedTotalRow.eachCell((cell, colNum) => {
        // Apply border
        cell.border = headerStyle.border;

        // Format: counts as integers, amounts with 2 decimals
        const isCountCol = (colNum - 3) % 2 === 0;
        const isAmountCol = (colNum - 3) % 2 === 1;

        if (isCountCol) {
            cell.numFmt = '#,##0'; // Thousands separator, no decimals
        } else if (isAmountCol) {
            cell.numFmt = '#,##0.00'; // Thousands separator, 2 decimal places
        }
        Object.assign(cell, totalStyle)
    });

    // Gender Summary Header
    sheet.addRow([]); // empty row
    const femaleRow = ['No of Membership', 'Female'];
    const maleRow = ['', 'Male'];
    dataArray.forEach(branch => {
        const member = branch.productMaleFemaleSummaryDtoList.find(p => normalize(p.productName) === 'MEMBER DEPOSIT');
        femaleRow.push(member?.femaleCount || 0);
        femaleRow.push(' ');
        maleRow.push(member?.maleCount || 0);
        maleRow.push(' ');
    });

    // Add rows and get references
    const femaleRowRef = sheet.addRow(femaleRow);
    const maleRowRef = sheet.addRow(maleRow);

    // Merge the "No of Membership" cell vertically across both rows
    sheet.mergeCells(`A${femaleRowRef.number}:A${maleRowRef.number}`);

    // Apply formatting to all cells
    [femaleRowRef, maleRowRef].forEach(row => {
        row.eachCell(cell => {
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
    });

    // Add total row with correct sum of both genders
    const genderTotal = ['Total', ''];
    for (let i = 2; i < femaleRow.length; i += 2) {
        const colLetter = sheet.getColumn(i + 1).letter;
        const femaleCellStart = femaleRowRef.number;
        const maleCellStart = maleRowRef.number;

        genderTotal.push({
            formula: `=SUM(${colLetter}${femaleCellStart}:${colLetter}${maleCellStart})`
        });
        genderTotal.push('');
    }

    const genderTotalRef = sheet.addRow(genderTotal);
    genderTotalRef.font = { bold: true };

    // Merge the first two cells in the total row
    sheet.mergeCells(`A${genderTotalRef.number}:B${genderTotalRef.number}`);

    // Apply total style
    genderTotalRef.eachCell(cell => {
        Object.assign(cell, totalStyle)
    });


    // Column widths
    sheet.columns.forEach((col, i) => col.width = i === 1 ? 30 : 20);
    sheet.views = [{ state: 'frozen', xSplit: 2, ySplit: 3 }];


    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, fileName);
}

function normalize(str) {
    return str.trim().toUpperCase();
}

function formatProductName(name) {
    return name.replace(/ +/g, ' ').trim().replace(/\s+DEPOSIT(S)?/, ' Deposits');
}
